import { useState } from 'react';
import JSZip from 'jszip';
import { Archive, Download, FileArchive, FolderArchive, X } from 'lucide-react';
import { ToolHeader } from '@/components/ui/ToolHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Dropzone } from '@/components/ui/Dropzone';
import { Field, Input } from '@/components/ui/Input';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/Tabs';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { formatBytes, readFileAsArrayBuffer } from '@/lib/utils';

interface ZipEntry {
  name: string;
  size: number;
  isDir: boolean;
  blob?: Blob;
}

export function ZipTool() {
  return (
    <div>
      <ToolHeader
        toolId="zip-tool"
        title="ZIP archives"
        description="Pack files into a ZIP, or open a ZIP and extract individual files."
      />
      <Tabs defaultValue="create">
        <TabsList className="mb-4">
          <TabsTrigger value="create">
            <FolderArchive size={11} className="mr-1.5" />
            Create
          </TabsTrigger>
          <TabsTrigger value="extract">
            <Archive size={11} className="mr-1.5" />
            Extract
          </TabsTrigger>
        </TabsList>
        <TabsContent value="create"><CreatePanel /></TabsContent>
        <TabsContent value="extract"><ExtractPanel /></TabsContent>
      </Tabs>
    </div>
  );
}

function CreatePanel() {
  const [files, setFiles] = useState<File[]>([]);
  const [name, setName] = useState('archive');
  const [working, setWorking] = useState(false);

  async function build() {
    if (!files.length) return;
    setWorking(true);
    try {
      const zip = new JSZip();
      for (const f of files) {
        const buf = await readFileAsArrayBuffer(f);
        zip.file(f.name, buf);
      }
      const blob = await zip.generateAsync({ type: 'blob', compression: 'DEFLATE', compressionOptions: { level: 6 } });
      saveAs(blob, `${name || 'archive'}.zip`);
      toast.success(`Packed ${files.length} files`);
    } catch {
      toast.error('Failed to create ZIP');
    } finally {
      setWorking(false);
    }
  }

  const totalSize = files.reduce((s, f) => s + f.size, 0);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="lg:col-span-2">
        <Dropzone
          multiple
          onFiles={(f) => setFiles((p) => [...p, ...f])}
          files={files}
          onRemove={(i) => setFiles((p) => p.filter((_, idx) => idx !== i))}
          hint="Any files — order doesn't matter"
        />
      </Card>
      <Card className="space-y-3">
        <Field label="Archive name">
          <Input value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <div className="text-xs text-fg-muted space-y-1">
          <div className="flex items-center justify-between">
            <span>Files</span>
            <span className="font-mono">{files.length}</span>
          </div>
          <div className="flex items-center justify-between">
            <span>Total size</span>
            <span className="font-mono">{formatBytes(totalSize)}</span>
          </div>
        </div>
        <Button
          variant="primary"
          className="w-full"
          loading={working}
          disabled={!files.length || working}
          onClick={build}
        >
          <Download size={11} />
          Pack ZIP
        </Button>
      </Card>
    </div>
  );
}

function ExtractPanel() {
  const [zip, setZip] = useState<JSZip | null>(null);
  const [zipName, setZipName] = useState('');
  const [entries, setEntries] = useState<ZipEntry[]>([]);
  const [working, setWorking] = useState(false);

  async function handleFile(files: File[]) {
    const f = files[0];
    if (!f) return;
    setZipName(f.name.replace(/\.zip$/i, ''));
    const buf = await readFileAsArrayBuffer(f);
    const z = await JSZip.loadAsync(buf);
    const list: ZipEntry[] = [];
    z.forEach((path, file) => {
      list.push({
        name: path,
        size: ((file as unknown as { _data?: { uncompressedSize?: number } })._data?.uncompressedSize) ?? 0,
        isDir: file.dir,
      });
    });
    list.sort((a, b) => a.name.localeCompare(b.name));
    setZip(z);
    setEntries(list);
  }

  async function extract(entry: ZipEntry) {
    if (!zip || entry.isDir) return;
    const file = zip.file(entry.name);
    if (!file) return;
    const blob = await file.async('blob');
    saveAs(blob, entry.name.split('/').pop() ?? entry.name);
  }

  async function extractAll() {
    if (!zip) return;
    setWorking(true);
    try {
      let count = 0;
      const fileEntries = entries.filter((e) => !e.isDir);
      for (const e of fileEntries) {
        const f = zip.file(e.name);
        if (!f) continue;
        const blob = await f.async('blob');
        saveAs(blob, e.name.split('/').pop() ?? e.name);
        count++;
      }
      toast.success(`Extracted ${count} files`);
    } finally {
      setWorking(false);
    }
  }

  return (
    <div>
      {!zip ? (
        <Dropzone
          accept=".zip,application/zip"
          onFiles={handleFile}
          icon={<FileArchive size={20} />}
          hint="ZIP archive only"
        />
      ) : (
        <Card className="!p-0 overflow-hidden">
          <div className="px-4 py-3 border-b border-border flex items-center justify-between">
            <div>
              <div className="text-sm font-medium">{zipName}.zip</div>
              <div className="text-2xs text-fg-subtle font-mono mt-0.5">
                {entries.filter((e) => !e.isDir).length} files
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button size="sm" variant="ghost" onClick={() => setZip(null)}>
                <X size={11} />
                Close
              </Button>
              <Button size="sm" variant="primary" onClick={extractAll} loading={working}>
                <Download size={11} />
                Extract all
              </Button>
            </div>
          </div>
          <div className="divide-y divide-border max-h-[480px] overflow-y-auto">
            {entries.map((e) => (
              <div
                key={e.name}
                className="flex items-center justify-between gap-3 px-4 py-2 hover:bg-surface-hover transition group"
              >
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-fg-subtle">
                    {e.isDir ? <FolderArchive size={12} /> : <FileArchive size={12} />}
                  </span>
                  <code className="text-xs font-mono truncate">{e.name}</code>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {!e.isDir && (
                    <span className="text-2xs font-mono text-fg-subtle">
                      {formatBytes(e.size)}
                    </span>
                  )}
                  {!e.isDir && (
                    <button
                      onClick={() => extract(e)}
                      className="opacity-0 group-hover:opacity-100 transition size-6 rounded text-fg-subtle hover:text-fg flex items-center justify-center"
                    >
                      <Download size={11} />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

export default ZipTool;
